/* iter-p3-w — Voice Rewrite Preview Dialog.
 *
 * Shown after `POST /projects/{pid}/chapters/{cid}/voice-rewrite`.
 * Renders the AI's rewrite side-by-side with the original last
 * paragraph and lets the writer Apply (in-place replace via the
 * chapter update API) or Copy.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle,
         DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy, Check, Loader2, Mic, GitCompare, List } from 'lucide-react';
import WordDiff from './WordDiff';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function VoiceRewritePreviewDialog({
  open, onOpenChange, chapter, project, rewrite, onApplied,
}) {
  const [applying, setApplying] = useState(false);
  const [copied,   setCopied]   = useState(false);
  const [view,     setView]     = useState('side'); // 'side' | 'diff'

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(rewrite?.rewritten || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error('Clipboard blocked — copy manually.'); }
  };

  const doApply = async () => {
    if (!project?.id || !chapter?.id || !rewrite?.rewritten) return;
    setApplying(true);
    try {
      // Compose new chapter content: replace ONLY the trailing N paragraphs.
      const current = chapter.content || '';
      const paras = current.split(/\n\n+/).filter((p) => p.trim().length);
      const keep = paras.slice(0, Math.max(0, paras.length - (rewrite.paragraphs || 1)));
      const newContent = [...keep, rewrite.rewritten.trim()].join('\n\n');
      await axios.put(`${API}/chapters/${chapter.id}`,
        { content: newContent },
        { withCredentials: true, timeout: 20000 });
      toast.success(`Ch ${chapter.number}: applied voice rewrite.`);
      onApplied?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Apply failed.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="voice-rewrite-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mic className="h-4 w-4 text-primary" />
            Voice rewrite · Ch {chapter?.number}
          </DialogTitle>
          <DialogDescription>
            Rewritten in <b>{rewrite?.character || 'the anchored character'}</b>'s
            Regional Voice preset. Review below and Apply to replace the last
            {rewrite?.paragraphs === 1 ? ' paragraph' : ` ${rewrite?.paragraphs || 1} paragraphs`} in the chapter.
          </DialogDescription>
        </DialogHeader>

        <div className="mb-1 flex items-center justify-between">
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
            Preview
          </div>
          <div className="inline-flex overflow-hidden rounded-md border border-border text-[10.5px]" data-testid="voice-rewrite-view-toggle">
            <button
              type="button"
              onClick={() => setView('side')}
              data-testid="voice-rewrite-view-side"
              className={`inline-flex items-center gap-1 px-2 py-1 font-semibold ${view === 'side' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <List className="h-3 w-3" /> Side-by-side
            </button>
            <button
              type="button"
              onClick={() => setView('diff')}
              data-testid="voice-rewrite-view-diff"
              className={`inline-flex items-center gap-1 px-2 py-1 font-semibold ${view === 'diff' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <GitCompare className="h-3 w-3" /> Word diff
            </button>
          </div>
        </div>

        {view === 'diff' ? (
          <div className="max-h-72 overflow-auto rounded-md border border-border bg-muted/20 p-3"
               data-testid="voice-rewrite-diff-view">
            <WordDiff before={rewrite?.original || ''} after={rewrite?.rewritten || ''} />
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <div className="mb-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">Original</div>
              <div className="max-h-72 overflow-auto rounded-md border border-border bg-muted/40 p-3 text-[12.5px] leading-relaxed whitespace-pre-wrap"
                   data-testid="voice-rewrite-original">
                {rewrite?.original || '—'}
              </div>
            </div>
            <div>
              <div className="mb-1 flex items-center justify-between text-[10.5px] uppercase tracking-wider">
                <span className="text-primary">Rewrite (in-region)</span>
                <button
                  type="button"
                  onClick={doCopy}
                  className="inline-flex items-center gap-1 rounded-sm border border-border px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground hover:border-primary hover:text-primary"
                  data-testid="voice-rewrite-copy"
                >
                  {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="max-h-72 overflow-auto rounded-md border border-primary/40 bg-primary/5 p-3 text-[12.5px] leading-relaxed whitespace-pre-wrap"
                   data-testid="voice-rewrite-rewritten">
                {rewrite?.rewritten || '—'}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange?.(false)} data-testid="voice-rewrite-cancel">
            Cancel
          </Button>
          <Button
            onClick={doApply}
            disabled={applying || !rewrite?.rewritten}
            data-testid="voice-rewrite-apply"
          >
            {applying ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Apply to Ch {chapter?.number}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
